import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button } from 'antd';

import { PLANS_PATH, PLANS_VISIBLE } from '../../config/plans';
import { planLimitOf, planLimitWords } from '../../utils/planLimit';

/**
 * THE plan-limit refusal (docs/platform-standards.md §7 "Plan limits"): one
 * antd `Alert` that says what limit was reached and what to do next, worded
 * for whoever met it — read from what the API chose to send them
 * (utils/planLimit.js), never from the client's idea of who owns what.
 *
 *   owner    "You've reached the limit of 10,000 items." · what still works ·
 *            "See plans" (only once plans are visible, config/plans.js)
 *   member   "This collection has reached its limit of 10,000 items." · what
 *            still works · "Ask the collection's owner."
 *   visitor  "This form isn't taking submissions right now." — nothing else
 *
 * No plan is ever named. `refusal` is the 409 body, or pass the axios `error`.
 * Renders nothing for anything that is not a plan-limit refusal, so a caller
 * can hand it whatever its last error was.
 *
 * A `below`-row element like `Guidance`: under what it is about, never in a
 * badge. On a phone the action drops under the words at the 44px floor
 * (plan-limit.css).
 */
export default function PlanLimitAlert({ refusal = null, error = null, onClose = null, className = '' }) {
	const body = refusal || planLimitOf(error);
	const words = body ? planLimitWords(body) : null;
	if (!words) return null;
	const action = words.audience === 'owner' && PLANS_VISIBLE ? (
		<Link to={PLANS_PATH} className="plan-limit__action">
			<Button type="link" size="small">See plans</Button>
		</Link>
	) : null;
	return (
		<Alert
			className={['plan-limit', className].filter(Boolean).join(' ')}
			type="warning"
			showIcon
			role="alert"
			message={words.title}
			description={words.detail || undefined}
			action={action}
			closable={!!onClose}
			onClose={onClose || undefined}
			data-testid="plan-limit"
			data-audience={words.audience}
		/>
	);
}
